import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  MyAssignmentsParams,
  MyAssignmentsResponse,
} from 'src/apis/assignment-service/types';
import type { OptionalAssignmentServiceRequest } from 'src/apis/assignment-service/use-optional-assignment-service';
import type { Successful } from 'src/hooks/use-axios';
import { useAxios } from 'src/hooks/use-axios';

type Props = {
  assignmentService?: Successful<OptionalAssignmentServiceRequest>;
  operatorId?: string | null;
};

type HookProps = {
  url: string;
} & MyAssignmentsParams;

export function useOptionalMyAssignments({
  assignmentService,
  operatorId,
}: Props) {
  const myAssignments = useAxios(
    (axios, baseConfig, { url, ...params }: HookProps) =>
      axios.request<MyAssignmentsResponse>({
        ...baseConfig,
        url,
        params,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (
      !assignmentService?.response.data._links.getMyAssignments ||
      !operatorId
    )
      return;

    const url = assignmentService.response.data._links.getMyAssignments.href;
    const templatedUrl = urlTemplate.parse(url).expand({});

    myAssignments.execute({
      url: templatedUrl,
      operatorId,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [assignmentService?.response, operatorId]);

  if (!assignmentService?.response.data._links.getMyAssignments || !operatorId)
    return;

  return myAssignments;
}

export type OptionalMyAssignmentsRequest = ReturnType<
  typeof useOptionalMyAssignments
>;

export function useManualMyAssignments() {
  const myAssignments = useAxios(
    (axios, baseConfig, { url, ...params }: HookProps) =>
      axios.request<MyAssignmentsResponse>({
        ...baseConfig,
        url,
        params,
      }),
    { neededOnPageLoad: false }
  );

  return myAssignments;
}

export type ManualMyAssignmentsRequest = ReturnType<
  typeof useManualMyAssignments
>;
